import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Info } from 'react-feather';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import objectHash from 'object-hash';
import { pathEq } from 'ramda';

import { OUTBOUND } from 'constants/booking';
import { seatShape } from 'constants/propTypes';
import currencySymbols from 'constants/currencies';
import { bookingStatusTypes, bookingStatus as bookingStatuses } from 'types/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import PassengerType from 'components/ui/PassengerType';
import { isESAndERThroughFare } from 'utils/summatedFares';
import { colours } from 'themes/default';

import BookingItemCheckBox from './BookingItemCheckBox';
import {
  CoachSeatButton,
  CoachSeatLabel,
  NoSeat,
  CostPriceTooltip,
  DependantTooltip,
  StatusTooltip,
  StatusTicket
} from './JourneyLegs.styles';
import { isTicketConflicted } from '../../../../utils/booking';
import { getInfantPassengersFromDetails } from 'utils/getInfantPassengersFromDetails';

export const mapToPassengersData = (product, index, leg, booking, bookingItemsCostPrice, intl, ticketDetails) => {
  const { OVERPAID, IN_AFTERSALES, CANCELLED, PENDING_REFUND, REFUNDED, PENDING_CHANGES, UNDERPAID } = bookingStatuses;
  const bookingStatus = booking.displayStatus;
  const { bookingItems, serviceType } = leg;
  const costPrice = bookingItemsCostPrice?.find((i) => i.id === product.uuid)?.costPrice;
  const {
    passenger: { id, firstName, lastName, type, disabilityType },
    status,
    currency,
    seat,
    price,
    discounts = [],
    loyaltyPoints,
    refundMethod
  } = product;

  const infants = getInfantPassengersFromDetails({ additionalDetails: booking.additionalDetails });
  const infantOnLap = infants.find((ad) => ad.passengerRef === id);

  const isEurostar = pathEq(['code'], serviceTypeCodes.EUROSTAR, serviceType);
  const isGreenspeed = pathEq(['code'], serviceTypeCodes.GREENSPEED, serviceType);
  const isCancelled = Boolean(status === bookingStatusTypes.CANCELLED || status === bookingStatusTypes.TEMPORARILY_CANCELLED);
  const isDisabled =
    isCancelled ||
    !(isEurostar || isGreenspeed) ||
    [OVERPAID, IN_AFTERSALES, CANCELLED, PENDING_REFUND, REFUNDED, PENDING_CHANGES, UNDERPAID].includes(bookingStatus);
  const isConflicted = isTicketConflicted(ticketDetails?.find((ticket) => ticket.ticketNumber === product.ticketNumber));

  const getTicketPrice = () =>
    booking.isLoyalty && product.tariffDetails?.bucketCode ? `${loyaltyPoints} pts` : `${currencySymbols[currency]}${price.toFixed(2)}`;

  const getTcnValue = () =>
    costPrice
      ? `${intl.formatMessage({ id: 'paymentsTab.tcnValue' })}: ${currencySymbols[costPrice.currency]}${costPrice.amount.toFixed(2)}`
      : '';

  const isDependant = booking.additionalDetails?.find((ad) => ad.passengerRef === id && ad.key === 'dependant' && ad.value === 'true');

  const cautionaryStatuses = ['suspended', 'placeholder', 'UKEntryDenied'];
  const statusColor = cautionaryStatuses.includes(status) ? colours.Core.SecondaryRed : colours.Greyscale.Black;

  return {
    key: objectHash(`${firstName}${lastName}${index}`),
    dataCy: `${id}-row`,
    passengerId: id.split('_')?.[1] || id,
    firstName,
    lastName,
    passengerType: (
      <>
        <DependantTooltip>
          <PassengerType type={type} disabilityType={disabilityType} />
          {isDependant && (
            <>
              &nbsp;
              <Info size={15} data-for="dependant-tooltip" data-tip />
            </>
          )}
          {infantOnLap && (
            <>
              <DependantTooltip>
                <>
                  &nbsp;
                  <Info size={15} data-for={`infant-tooltip-${infantOnLap.infant_first_name}`} data-tip />
                </>
              </DependantTooltip>

              <ReactTooltip
                className="tooltip"
                id={`infant-tooltip-${infantOnLap.infant_first_name}`}
                data-cy={`infant-tooltip-${infantOnLap.infant_first_name}`}
                place="bottom"
              >
                <FormattedMessage
                  id={'bookingDetails.infantsOnLapDetails'}
                  values={{ infantFirstName: infantOnLap.infant_first_name, infantLastName: infantOnLap.infant_last_name }}
                />
              </ReactTooltip>
            </>
          )}
        </DependantTooltip>

        <ReactTooltip className="tooltip" id="dependant-tooltip" data-cy="dependant-tooltip" place="bottom">
          <FormattedMessage id="paxType.dependant" />
        </ReactTooltip>
      </>
    ),
    coachSeat: { seat, isDisabled, isConflicted },
    ticketNumber: product.ticketNumber,
    ticketPrice: (
      <>
        {costPrice ? (
          <>
            <CostPriceTooltip data-for="cost-price-tooltip" data-tip={getTcnValue()}>
              {getTicketPrice()}
              <Info size={15} />
            </CostPriceTooltip>

            <ReactTooltip id="cost-price-tooltip" data-cy="cost-price-tooltip" />
          </>
        ) : (
          `${getTicketPrice()} `
        )}
        {discounts?.length > 0 && <FormattedMessage id="journeySegment.discounted" />}
      </>
    ),
    status: refundMethod ? (
      <>
        <StatusTooltip data-for="status-tooltip" data-tip={refundMethod}>
          <FormattedMessage id={`bookingStatus.${status}`} />
          <Info size={15} />
        </StatusTooltip>
        <ReactTooltip id="status-tooltip" data-cy="status-tooltip" />
      </>
    ) : (
      <StatusTicket color={statusColor}>
        <FormattedMessage id={`bookingStatus.${status}`} />
      </StatusTicket>
    ),
    select: { bookingItems, i: index }
  };
};

export const formatSeat = (targetUrl, pathname, isSelectable = true) => {
  const FormatSeat = ({ seat, isDisabled, isConflicted }) => {
    const seatNumber = seat && seat.number;
    const seatCarriage = seat && seat.carriage;

    if (seatCarriage && seatNumber && !isSelectable) {
      return <CoachSeatLabel>{`${seatCarriage} | ${seatNumber}`}</CoachSeatLabel>;
    }

    if (seatCarriage && seatNumber) {
      return (
        <CoachSeatButton
          id={`coach-seat-number-${seatCarriage}-${seatNumber}`}
          label={`${seatCarriage} / ${seatNumber}`}
          element={<Link to={{ pathname: targetUrl, state: { from: pathname } }} />}
          disabled={isDisabled}
          isConflicted={isConflicted}
        />
      );
    }

    return (
      <NoSeat>
        <div>--</div>
        <div>--</div>
      </NoSeat>
    );
  };

  FormatSeat.propTypes = {
    seat: seatShape,
    isDisabled: PropTypes.bool.isRequired,
    isConflicted: PropTypes.bool.isRequired
  };

  return FormatSeat;
};

export const getWithExchangedProducts = ({ direction, outboundWithExchangedProducts, inboundWithExchangedProducts }) =>
  direction === OUTBOUND ? outboundWithExchangedProducts : inboundWithExchangedProducts;

export const getColumns = ({
  targetUrl,
  pathname,
  isGroupChecked,
  isSelectable = true,
  toggleGroup,
  legBookingItems,
  addOnLeg,
  isMultiInventory,
  isMultiJourney,
  booking,
  isOutboundSummatedFare,
  isInboundSummatedFare,
  selectItemIndependently
}) =>
  [
    isSelectable && { key: 'passengerId' },
    { key: 'firstName' },
    { key: 'lastName' },
    { key: 'passengerType' },
    { key: 'coachSeat', format: formatSeat(targetUrl, pathname, isSelectable) },
    { key: 'ticketPrice' },
    { key: 'ticketNumber' },
    { key: 'status' },
    isSelectable && {
      key: 'select',
      // eslint-disable-next-line react/prop-types
      format: ({ bookingItems, i }) => (
        <BookingItemCheckBox
          booking={booking}
          isMultiInventory={isMultiInventory}
          isMultiJourney={isMultiJourney}
          legBookingItems={legBookingItems}
          bookingItem={bookingItems[i]}
          isGroupChecked={isGroupChecked}
          toggleGroup={toggleGroup}
          addOnLeg={addOnLeg}
          isOutboundSummatedFare={isOutboundSummatedFare}
          isInboundSummatedFare={isInboundSummatedFare}
          selectItemIndependently={selectItemIndependently}
        />
      )
    }
  ].filter(Boolean);

export const findAllItemRefsOnSameLeg = ({ segment, itemRef }) =>
  segment
    ?.filter(({ products }) => products?.some((p) => p.itemRef === itemRef))
    ?.flatMap(({ products }) => products)
    ?.flatMap(({ itemRef }) => itemRef);

export const applyCheckBoxRules = ({
  segments = [],
  legBookingItems = [],
  isSummatedFare = false,
  isMultiInventory = false,
  isMultiJourney = false,
  associatedItemReferences = [],
  itemRef = '',
  addOnItemRefs = []
}) => {
  const isESERThroughFare = isESAndERThroughFare({ segment: segments, itemRefs: itemRef });
  const allItemRefsOnSameLeg = findAllItemRefsOnSameLeg({ segment: segments, itemRef });

  const areItemRefsPartOfAnUnsplittableLeg = segments
    .filter(({ isSplitPassengerEnabled }) => !isSplitPassengerEnabled)
    .flatMap(({ products }) => products)
    .map(({ itemRef }) => itemRef)
    .some((ref) => [itemRef].includes(ref));

  if (isESERThroughFare) return associatedItemReferences;

  if (isSummatedFare) {
    return areItemRefsPartOfAnUnsplittableLeg ? allItemRefsOnSameLeg : [itemRef];
  }

  if (segments.length <= 2 && isMultiInventory) return legBookingItems.map((i) => i.itemRef);

  if (isMultiJourney) return [itemRef];

  return associatedItemReferences.filter((ref) => !addOnItemRefs.includes(ref));
};
